// frontend/src/pages/GoogleCallbackPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const COMPONENT_NAME = 'GoogleCallbackPage';

const GoogleCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, setAuthError } = useAuth();
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleGoogleCallback = async () => {
      try {
        // Extract token and user data from URL parameters
        const token = searchParams.get('token');
        const userDataString = searchParams.get('user');
        const errorParam = searchParams.get('error');
        const isNewUser = searchParams.get('isNewUser') === 'true';

        // Check for error in URL
        if (errorParam) {
          const errorMessage = decodeURIComponent(errorParam);
          logger.error(`[${COMPONENT_NAME}] Error from backend:`, errorMessage);
          setError(errorMessage);
          setAuthError(errorMessage);
          setIsProcessing(false);
          
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        // Validate required parameters
        if (!token || !userDataString) {
          const errorMessage = 'Missing authentication data. Please try again.';
          logger.error(`[${COMPONENT_NAME}] Missing token or user data in callback URL`);
          setError(errorMessage);
          setAuthError(errorMessage);
          setIsProcessing(false);
          
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        // Parse user data
        let userData;
        try {
          userData = JSON.parse(decodeURIComponent(userDataString));
          
          console.log('========================================');
          console.log('🔍 GOOGLE CALLBACK - USER DATA FROM BACKEND');
          console.log('========================================');
          console.log('Full userData object:', JSON.stringify(userData, null, 2));
          console.log('User ID:', userData.id);
          console.log('Username:', userData.username);
          console.log('Email:', userData.email);
          console.log('---');
          console.log('Profile Fields (snake_case):');
          console.log('  first_name:', userData.first_name);
          console.log('  last_name:', userData.last_name);
          console.log('  phone_number:', userData.phone_number);
          console.log('---');
          console.log('Profile Fields (camelCase):');
          console.log('  firstName:', userData.firstName);
          console.log('  lastName:', userData.lastName);
          console.log('  phoneNumber:', userData.phoneNumber);
          console.log('========================================');
          
        } catch (parseError) {
          logger.error(`[${COMPONENT_NAME}] Failed to parse user data:`, parseError);
          throw new Error('Invalid user data received');
        }

        logger.info(`[${COMPONENT_NAME}] Google OAuth successful. Logging in user:`, userData.username);

        // IMPORTANT: Clear any old user data from localStorage before login
        localStorage.removeItem('user');
        localStorage.removeItem('token');

        // Call the login function with token and user data
        await login({ user: userData, token }, true);

        logger.info(`[${COMPONENT_NAME}] Login successful.`);
        
        // Check if user profile is complete - check BOTH formats and ensure not empty strings
        const hasFirstName = Boolean(
          (userData.first_name && userData.first_name.trim()) || 
          (userData.firstName && userData.firstName.trim())
        );
        const hasLastName = Boolean(
          (userData.last_name && userData.last_name.trim()) || 
          (userData.lastName && userData.lastName.trim())
        );
        const hasPhoneNumber = Boolean(
          (userData.phone_number && userData.phone_number.trim()) || 
          (userData.phoneNumber && userData.phoneNumber.trim())
        );
        
        const needsOnboarding = !hasFirstName || !hasLastName || !hasPhoneNumber;
        
        console.log('========================================');
        console.log('🔍 ONBOARDING CHECK RESULTS');
        console.log('========================================');
        console.log('hasFirstName:', hasFirstName);
        console.log('hasLastName:', hasLastName);
        console.log('hasPhoneNumber:', hasPhoneNumber);
        console.log('needsOnboarding:', needsOnboarding);
        console.log('isNewUser:', isNewUser);
        console.log('========================================');
        
        logger.info(`[${COMPONENT_NAME}] Profile completeness check:`, {
          hasFirstName,
          hasLastName,
          hasPhoneNumber,
          needsOnboarding,
          isNewUser
        });
        
        // Show onboarding if profile is incomplete
        if (needsOnboarding) {
          logger.info(`[${COMPONENT_NAME}] User profile incomplete. Redirecting to onboarding page.`);
          console.log('➡️  Redirecting to /onboarding');
          navigate('/onboarding', { replace: true });
          return;
        }
        
        // Profile is complete - go to home
        logger.info(`[${COMPONENT_NAME}] User profile complete. Redirecting to home.`);
        console.log('➡️  Redirecting to / (home)');
        navigate('/', { replace: true });


      } catch (err) {
        logger.error(`[${COMPONENT_NAME}] Error processing Google callback:`, err);
        const errorMessage = err.message || 'Failed to complete authentication. Please try again.';
        setError(errorMessage);
        setAuthError(errorMessage);
        setIsProcessing(false);
        
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleGoogleCallback();
  }, [searchParams, login, navigate, setAuthError]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col justify-center items-center py-12 px-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl rounded-xl p-8 sm:p-10 space-y-6">
        <div className="text-center">
          <img
            src="/connectsphere_logo_transparent.png"
            alt="ConnectSphere Logo"
            className="mx-auto h-14 w-auto mb-6"
          />
          
          {isProcessing && !error && (
            <>
              <div className="flex justify-center mb-4">
                <svg className="animate-spin h-12 w-12 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Completing Sign In...
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we set up your account
              </p>
            </>
          )}

          {error && (
            <>
              <div className="flex justify-center mb-4">
                <svg className="h-12 w-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Authentication Failed
              </h2>
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-md text-sm mb-4">
                {error}
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Redirecting to login page...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCallbackPage;
